/**
 * BounceCenterSection — P0 Sprint (2 Aug 2026, Iteratie B1).
 *
 * Operationeel Bounce Management Center. Toont:
 *   * KPI's per tijdsfilter (today / 7d / 30d / 90d)
 *   * Sample-size guard — waarschuwt bij kleine steekproef
 *   * Segmenten: All / Hard / Soft / Deferred / Blocked / Complaint /
 *     Suppressed / Retry Pending / Resolved
 *   * Per rij: recipient, subject, provider_id, delivery_state, retries
 *   * Acties: Retry (RBAC), Correct e-mail, Mark resolved, Suppression
 *
 * Backend: /api/admin/email-delivery/stats + /bounces + /bounces/{id}
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Loader2, RefreshCw, Mail, Ban, Bell,
  Search, ExternalLink, ShieldCheck, Info, X,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";

const SEGMENTS = [
  { key: "all",           label: "Alle problemen" },
  { key: "hard",          label: "Hard Bounce" },
  { key: "soft",          label: "Soft Bounce" },
  { key: "deferred",      label: "Deferred" },
  { key: "blocked",       label: "Geblokkeerd" },
  { key: "complained",    label: "Spam-klacht" },
  { key: "suppressed",    label: "Onderdrukt" },
  { key: "retry_pending", label: "Retry gepland" },
  { key: "resolved",      label: "Opgelost" },
];

const WINDOWS = [
  { key: "today", label: "Vandaag" },
  { key: "7d",    label: "7 dagen" },
  { key: "30d",   label: "30 dagen" },
  { key: "90d",   label: "90 dagen" },
  { key: "all",   label: "Alle tijd (historisch)" },
];

const STATE_STYLES = {
  hard_bounced:   "bg-red-100 text-red-900 border-red-200",
  soft_bounced:   "bg-amber-100 text-amber-900 border-amber-200",
  deferred:       "bg-yellow-100 text-yellow-900 border-yellow-200",
  blocked:        "bg-purple-100 text-purple-900 border-purple-200",
  complained:     "bg-orange-100 text-orange-900 border-orange-200",
  suppressed:     "bg-slate-200 text-slate-800 border-slate-300",
  retry_pending:  "bg-indigo-100 text-indigo-900 border-indigo-200",
  delivered:      "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed:         "bg-red-50 text-red-800 border-red-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function BounceCenterSection() {
  const { hasPermission } = useAuth();
  const canRetry = hasPermission("email_delivery:retry")
                     || hasPermission("communication:manage");
  const canSuppress = hasPermission("email_delivery:suppress");
  const canCorrect = hasPermission("email_delivery:correct_recipient")
                      || hasPermission("communication:manage");

  const [window, setWindow] = useState("30d");
  const [segment, setSegment] = useState("all");
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [rca, setRca] = useState(null);
  const [rcaOpen, setRcaOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsR, listR] = await Promise.all([
        adminApi.get(`/admin/email-delivery/stats?window=${window}`),
        adminApi.get(
          `/admin/email-delivery/bounces?window=${window}&segment=${segment}&limit=100`,
        ),
      ]);
      setStats(statsR.data);
      setItems(listR.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Bounce Center laden mislukt"));
    } finally {
      setLoading(false);
    }
  }, [window, segment]);
  useEffect(() => { load(); }, [load]);

  const showRca = async () => {
    try {
      const r = await adminApi.get("/admin/email-delivery/analysis");
      setRca(r.data);
      setRcaOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const term = q.trim().toLowerCase();
    return items.filter((it) =>
      (it.recipient_email || "").toLowerCase().includes(term)
      || (it.subject || "").toLowerCase().includes(term)
      || (it.provider_id || "").toLowerCase().includes(term)
      || (it.bounce_reason || "").toLowerCase().includes(term));
  }, [items, q]);

  return (
    <div className="space-y-4" data-testid="bounce-center-section">
      <div className="flex items-baseline gap-3">
        <h1 className="font-chivo font-black text-2xl text-[#082c50]"
            data-testid="bounce-center-title">
          Bounce Management Center
        </h1>
        <span className="text-xs uppercase tracking-wider font-bold text-slate-500">
          E-mail deliverability
        </span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm"
                  onClick={showRca}
                  className="rounded-none text-xs"
                  data-testid="bounce-center-rca-btn">
            <Info className="w-3 h-3 mr-1" /> Root Cause Analyse
          </Button>
          <Button variant="outline" size="sm"
                  onClick={load}
                  className="rounded-none text-xs"
                  data-testid="bounce-center-reload">
            <RefreshCw className="w-3 h-3 mr-1" /> Vernieuwen
          </Button>
        </div>
      </div>

      {/* KPI-strip */}
      {stats && <KpiStrip stats={stats} />}

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-3 flex flex-wrap gap-2 items-center">
        <div>
          <Label className="text-[10px] uppercase font-bold text-slate-600">Tijdsperiode</Label>
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="rounded-none h-8 w-44 text-xs"
                           data-testid="bounce-center-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.key} value={w.key}
                            data-testid={`bounce-center-window-${w.key}`}>
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase font-bold text-slate-600">Segment</Label>
          <Select value={segment} onValueChange={setSegment}>
            <SelectTrigger className="rounded-none h-8 w-52 text-xs"
                           data-testid="bounce-center-segment">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEGMENTS.map((s) => (
                <SelectItem key={s.key} value={s.key}
                            data-testid={`bounce-center-segment-${s.key}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase font-bold text-slate-600">Zoeken</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="E-mail / onderwerp / provider_id / reden…"
                   className="rounded-none h-8 pl-7 text-xs"
                   data-testid="bounce-center-search" />
          </div>
        </div>
      </div>

      {/* Lijst */}
      <div className="bg-white border border-slate-200">
        <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-700">
            {filtered.length} record{filtered.length === 1 ? "" : "s"}
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm italic"
               data-testid="bounce-center-empty">
            {loading ? "Laden…" : "Geen records — kies een breder venster of ander segment."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1200px]">
              <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="text-left p-2">Ontvanger</th>
                  <th className="text-left p-2">Onderwerp</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Reden</th>
                  <th className="text-left p-2">Retries</th>
                  <th className="text-left p-2">Laatste event</th>
                  <th className="text-right p-2">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((it) => (
                  <tr key={it.id} data-testid={`bounce-row-${it.id}`}>
                    <td className="p-2">
                      <div className="font-mono text-[11px] text-[#082c50] break-all">{it.recipient_email || "—"}</div>
                      {it.customer_id && <div className="text-[10px] text-slate-500">klant: {it.customer_id.slice(0, 8)}…</div>}
                    </td>
                    <td className="p-2 max-w-xs truncate" title={it.subject}>{it.subject || "—"}</td>
                    <td className="p-2">
                      <span className={`inline-block px-2 py-0.5 border text-[10px] uppercase font-bold ${
                        STATE_STYLES[it.delivery_state] || "bg-slate-100 text-slate-800 border-slate-200"
                      }`}
                            data-testid={`bounce-row-state-${it.id}`}>
                        {it.delivery_state_label || it.delivery_state}
                      </span>
                    </td>
                    <td className="p-2 max-w-[250px] truncate text-slate-600" title={it.bounce_reason}>
                      {it.bounce_reason || <span className="italic text-slate-400">—</span>}
                    </td>
                    <td className="p-2 text-center font-mono">{it.retries_attempted || 0}</td>
                    <td className="p-2 text-slate-500">{fmtDate(it.last_event_at || it.created_at)}</td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost"
                              onClick={() => setDetail(it)}
                              className="h-6 px-2 rounded-none text-[10px]"
                              data-testid={`bounce-row-detail-${it.id}`}>
                        <ExternalLink className="w-3 h-3 mr-1" /> Detail
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <BounceDetailDialog
        item={detail}
        onClose={() => setDetail(null)}
        onReload={load}
        canRetry={canRetry}
        canSuppress={canSuppress}
        canCorrect={canCorrect}
      />

      {/* RCA dialog */}
      <RcaDialog open={rcaOpen} onClose={() => setRcaOpen(false)} rca={rca} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// KPI strip
// ═══════════════════════════════════════════════════════════════
function KpiStrip({ stats }) {
  const isSmallSample = !stats.sample_reliable;
  return (
    <div className="space-y-2">
      {isSmallSample && stats.real_sends > 0 && (
        <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
             data-testid="bounce-center-small-sample-warning">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Kleine steekproef ({stats.real_sends} verzendpogingen) — absolute
            aantallen tonen; percentages kunnen misleidend zijn.
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <Kpi label="Verzonden" value={stats.real_sends} testId="kpi-real-sends" />
        <Kpi label="Afgeleverd" value={stats.delivered_ok} testId="kpi-delivered" tone="emerald" />
        <Kpi label="Delivery %" value={stats.delivery_rate !== null ? `${stats.delivery_rate}%` : "—"}
             testId="kpi-delivery-rate"
             tone={
               stats.delivery_rate == null ? "gray"
               : stats.delivery_rate >= 99 ? "emerald"
               : stats.delivery_rate >= 97 ? "amber"
               : "red"
             } />
        <Kpi label="Hard bounces" value={stats.hard_bounces} testId="kpi-hard-bounces"
             tone={stats.hard_bounces > 0 ? "red" : "gray"} />
        <Kpi label="Soft bounces" value={stats.counts_by_state?.soft_bounced || 0} testId="kpi-soft-bounces" tone="amber" />
        <Kpi label="Complaints" value={stats.complaints} testId="kpi-complaints"
             tone={stats.complaints > 0 ? "orange" : "gray"} />
        <Kpi label="Retry queue" value={stats.counts_by_state?.retry_pending || 0}
             testId="kpi-retry-queue" tone="indigo" />
      </div>
      <div className={`text-xs px-3 py-2 border ${
        stats.health_status === "healthy" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
        : stats.health_status === "warning" ? "bg-amber-50 border-amber-200 text-amber-900"
        : "bg-red-50 border-red-200 text-red-900"
      }`}
           data-testid="bounce-center-health-verdict">
        <strong className="uppercase tracking-wide">{stats.health_status}</strong> — {stats.health_message}
      </div>
    </div>
  );
}
function Kpi({ label, value, testId, tone }) {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    red:     "bg-red-50 border-red-200 text-red-900",
    orange:  "bg-orange-50 border-orange-200 text-orange-900",
    indigo:  "bg-indigo-50 border-indigo-200 text-indigo-900",
    gray:    "bg-slate-50 border-slate-200 text-slate-800",
  }[tone] || "bg-white border-slate-200 text-slate-800";
  return (
    <div className={`border ${cls} px-3 py-2`} data-testid={testId}>
      <div className="text-[9px] font-bold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-black font-mono">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Bounce Detail Dialog
// ═══════════════════════════════════════════════════════════════
function BounceDetailDialog({ item, onClose, onReload, canRetry, canSuppress, canCorrect }) {
  const [correctOpen, setCorrectOpen] = useState(false);
  const [suppressOpen, setSuppressOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item) return;
    (async () => {
      setLoading(true);
      try {
        const r = await adminApi.get(`/admin/email-delivery/bounces/${item.id}`);
        setDetail(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [item]);

  const markResolved = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/email-delivery/bounces/${item.id}/mark-resolved`, {});
      toast.success("Gemarkeerd als opgelost");
      onReload(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!item) return null;
  const rec = detail?.record || item;
  const events = detail?.events || [];
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bounce-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#082c50]">
            <Mail className="w-5 h-5" /> Bounce detail
          </DialogTitle>
          <DialogDescription>
            {rec.recipient_email} · {detail?.delivery_state_label || item.delivery_state_label}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-6 text-center text-slate-500">
            <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Laden…
          </div>
        ) : (
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <Info2 label="Provider ID" value={rec.provider_id || "—"} mono />
              <Info2 label="Status" value={rec.status || "—"} />
              <Info2 label="Delivery state" value={detail?.delivery_state || "—"} />
              <Info2 label="Verzonden op" value={fmtDate(rec.created_at)} />
              <Info2 label="Retries" value={rec.retries_attempted || 0} />
              <Info2 label="Volgende retry" value={fmtDate(rec.next_retry_at)} />
            </div>
            {rec.bounce_reason && (
              <div className="bg-red-50 border border-red-200 p-2 text-red-900">
                <div className="text-[10px] font-bold uppercase">Bounce reden</div>
                <div className="mt-1">{rec.bounce_reason}</div>
              </div>
            )}
            {rec.correction_applied_at && (
              <div className="bg-blue-50 border border-blue-200 p-2 text-blue-900">
                <div className="text-[10px] font-bold uppercase">Correctie toegepast</div>
                <div className="mt-1">
                  {rec.correction_from} → <strong>{rec.correction_to}</strong> · {fmtDate(rec.correction_applied_at)}
                </div>
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 p-2 max-h-40 overflow-y-auto">
              <div className="text-[10px] font-bold uppercase mb-1">Event history ({events.length})</div>
              {events.length === 0 ? <div className="italic text-slate-400">Geen events</div>
                : events.map((ev, i) => (
                <div key={i} className="text-[11px] flex items-center gap-2">
                  <span className="font-bold">{ev.type || ev.event || "—"}</span>
                  <span className="text-slate-500">{fmtDate(ev.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter className="gap-1 flex-wrap">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-none text-xs">
            Sluiten
          </Button>
          {canRetry && detail?.delivery_state !== "hard_bounced" && (
            <Button size="sm" onClick={() => setRetryOpen(true)} disabled={busy}
                    className="rounded-none text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="bounce-detail-retry-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          )}
          {canCorrect && (
            <Button size="sm" onClick={() => setCorrectOpen(true)} disabled={busy}
                    variant="outline" className="rounded-none text-xs"
                    data-testid="bounce-detail-correct-btn">
              <Mail className="w-3 h-3 mr-1" /> Corrigeer e-mail
            </Button>
          )}
          {canSuppress && (
            <Button size="sm" onClick={() => setSuppressOpen(true)} disabled={busy}
                    variant="outline" className="rounded-none text-xs"
                    data-testid="bounce-detail-suppress-btn">
              <Ban className="w-3 h-3 mr-1" /> Suppressie toevoegen
            </Button>
          )}
          <Button size="sm" onClick={markResolved} disabled={busy}
                  variant="outline" className="rounded-none text-xs"
                  data-testid="bounce-detail-resolve-btn">
            <ShieldCheck className="w-3 h-3 mr-1" /> Markeer opgelost
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Sub-dialogs */}
      <CorrectRecipientDialog open={correctOpen} onClose={() => setCorrectOpen(false)}
                               item={item} onDone={() => { onReload(); onClose(); }} />
      <SuppressDialog open={suppressOpen} onClose={() => setSuppressOpen(false)}
                       item={item} onDone={() => { onReload(); }} />
      <RetryDialog open={retryOpen} onClose={() => setRetryOpen(false)}
                    item={item} onDone={() => { onReload(); onClose(); }} />
    </Dialog>
  );
}

function Info2({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase font-bold text-slate-600">{label}</div>
      <div className={`text-xs ${mono ? "font-mono" : ""} text-slate-900 mt-0.5 break-all`}>
        {value}
      </div>
    </div>
  );
}

function CorrectRecipientDialog({ open, onClose, item, onDone }) {
  const [newEmail, setNewEmail] = useState("");
  const [reason, setReason] = useState("");
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.post(
        `/admin/email-delivery/bounces/${item.id}/correct-recipient`,
        { new_email: newEmail, reason, resend },
      );
      toast.success("E-mailadres gecorrigeerd");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="correct-recipient-dialog">
        <DialogHeader>
          <DialogTitle>Corrigeer e-mailadres</DialogTitle>
          <DialogDescription>
            Origineel: <span className="font-mono">{item?.recipient_email}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          <Label>Nieuw e-mailadres</Label>
          <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                 type="email" className="rounded-none h-9"
                 data-testid="correct-recipient-new-email" />
          <Label>Reden</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 className="rounded-none h-9" maxLength={500}
                 data-testid="correct-recipient-reason" />
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)}
                   data-testid="correct-recipient-resend" />
            Ook nieuw bericht verzenden naar dit adres
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuleren</Button>
          <Button onClick={submit} disabled={busy || !newEmail}
                  className="rounded-none bg-[#082c50] text-white"
                  data-testid="correct-recipient-submit">
            Bevestigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuppressDialog({ open, onClose, item, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/email-delivery/suppressions",
        { email: item.recipient_email, reason, source: "manual_bounce_center" },
      );
      toast.success("Suppressie toegevoegd");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="suppress-dialog">
        <DialogHeader>
          <DialogTitle>Suppressie toevoegen</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{item?.recipient_email}</span> wordt geblokkeerd voor niet-transactionele mail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          <Label>Reden</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 className="rounded-none h-9" maxLength={500}
                 data-testid="suppress-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuleren</Button>
          <Button onClick={submit} disabled={busy}
                  className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                  data-testid="suppress-submit">
            Onderdrukken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RetryDialog({ open, onClose, item, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/email-delivery/bounces/${item.id}/retry`, { reason });
      toast.success("Retry aangevraagd");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="retry-dialog">
        <DialogHeader>
          <DialogTitle>Retry inplannen</DialogTitle>
          <DialogDescription>
            Bericht wordt opnieuw verzonden bij de volgende scheduler-run.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          <Label>Reden</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 className="rounded-none h-9" maxLength={500}
                 data-testid="retry-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuleren</Button>
          <Button onClick={submit} disabled={busy}
                  className="rounded-none bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="retry-submit">
            Bevestigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════
// RCA Dialog — verklaart waar de "90 bounces" vandaan komen
// ═══════════════════════════════════════════════════════════════
function RcaDialog({ open, onClose, rca }) {
  if (!rca) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="rca-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#082c50]">
            <Info className="w-5 h-5" /> Root Cause Analyse — E-mail bounces
          </DialogTitle>
          <DialogDescription>
            Waar komen de bounces vandaan? Uitsplitsing per tijdsvenster.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="bg-indigo-50 border border-indigo-200 p-3">
            <div className="text-[10px] uppercase font-bold text-indigo-800 mb-1">Root cause</div>
            <div className="text-indigo-900">{rca.root_cause_summary}</div>
          </div>
          <div className="bg-white border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-600">
                <tr>
                  <th className="text-left p-2">Venster</th>
                  <th className="text-right p-2">Records</th>
                  <th className="text-right p-2">Verzonden</th>
                  <th className="text-right p-2">Afgeleverd</th>
                  <th className="text-right p-2">Hard</th>
                  <th className="text-right p-2">Soft</th>
                  <th className="text-right p-2">Blocked</th>
                  <th className="text-right p-2">Complaint</th>
                  <th className="text-right p-2">Dup events</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.values(rca.windows).map((w) => (
                  <tr key={w.window} data-testid={`rca-window-${w.window}`}>
                    <td className="p-2 font-bold uppercase">{w.window}</td>
                    <td className="p-2 text-right font-mono">{w.total_records}</td>
                    <td className="p-2 text-right font-mono">{w.real_sends}</td>
                    <td className="p-2 text-right font-mono text-emerald-700">{w.delivered_ok}</td>
                    <td className="p-2 text-right font-mono text-red-700">{w.hard_bounces}</td>
                    <td className="p-2 text-right font-mono text-amber-700">{w.soft_bounces}</td>
                    <td className="p-2 text-right font-mono text-purple-700">{w.blocked}</td>
                    <td className="p-2 text-right font-mono text-orange-700">{w.complaints}</td>
                    <td className="p-2 text-right font-mono">{w.duplicate_events_seen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-3">
            <div className="text-[10px] uppercase font-bold text-amber-800 mb-1">Aanbeveling</div>
            <div className="text-amber-900">{rca.recommendation}</div>
          </div>
          <div className={`px-3 py-2 border ${
            rca.current_health_today.status === "healthy" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : rca.current_health_today.status === "warning" ? "bg-amber-50 border-amber-200 text-amber-900"
            : "bg-red-50 border-red-200 text-red-900"
          }`}>
            <div className="text-[10px] uppercase font-bold">Actuele status (vandaag)</div>
            <div className="mt-1">
              <strong className="uppercase">{rca.current_health_today.status}</strong> — {rca.current_health_today.message}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
